import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, Filter, ChevronDown, ChevronRight, AlertCircle, CheckCircle2, Pause, Play, Search, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REFRESH_INTERVAL_MS = 5000;

function StatusBadge({ code }) {
  const c = code || 0;
  let cls = 'bg-gray-100 text-gray-700';
  if (c >= 500) cls = 'bg-red-100 text-red-800';
  else if (c >= 400) cls = 'bg-amber-100 text-amber-800';
  else if (c >= 200 && c < 300) cls = 'bg-emerald-100 text-emerald-800';
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-mono font-semibold ${cls}`} data-testid="merchant-log-status-badge">
      {c}
    </span>
  );
}

function MethodBadge({ method }) {
  const m = (method || 'GET').toUpperCase();
  const palette = {
    GET: 'bg-blue-50 text-blue-700 border-blue-200',
    POST: 'bg-green-50 text-green-700 border-green-200',
    PUT: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    DELETE: 'bg-red-50 text-red-700 border-red-200',
    PATCH: 'bg-purple-50 text-purple-700 border-purple-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold border ${palette[m] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
      {m}
    </span>
  );
}

function PrettyJson({ value }) {
  if (value == null) return <span className="text-gray-400 text-xs italic">(vacío)</span>;
  if (typeof value !== 'string') {
    try {
      return <pre className="text-[11px] whitespace-pre-wrap break-words bg-slate-900 text-slate-100 p-3 rounded font-mono leading-relaxed max-h-96 overflow-auto">{JSON.stringify(value, null, 2)}</pre>;
    } catch (e) {
      return <pre className="text-[11px] text-red-700">No se pudo serializar</pre>;
    }
  }
  return <pre className="text-[11px] whitespace-pre-wrap break-words bg-slate-900 text-slate-100 p-3 rounded font-mono leading-relaxed max-h-96 overflow-auto">{value}</pre>;
}

function LogRow({ log, expanded, onToggle }) {
  const meta = log.metadata || {};
  const created = new Date(log.created_at);
  const ts = created.toLocaleString('es-VE', { hour12: false });
  return (
    <>
      <tr
        onClick={onToggle}
        className={`cursor-pointer hover:bg-slate-50 ${expanded ? 'bg-slate-50' : ''}`}
        data-testid={`merchant-log-row-${log.log_id}`}
      >
        <td className="px-3 py-2 text-[11px] text-gray-500 font-mono whitespace-nowrap">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {ts}
          </div>
        </td>
        <td className="px-3 py-2"><MethodBadge method={meta.method} /></td>
        <td className="px-3 py-2 text-[12px] font-mono text-gray-800 max-w-[280px] truncate" title={meta.path}>{meta.path}</td>
        <td className="px-3 py-2"><StatusBadge code={meta.status_code} /></td>
        <td className="px-3 py-2 text-[11px] text-gray-600 whitespace-nowrap">{meta.duration_ms ?? '-'} ms</td>
        <td className="px-3 py-2 text-[11px] text-gray-700 max-w-[180px] truncate" title={meta.project_name || meta.project_id || '-'}>
          {meta.project_name || (meta.project_id ? meta.project_id.slice(0, 8) + '…' : '-')}
        </td>
        <td className="px-3 py-2 text-[11px] text-gray-600 font-mono whitespace-nowrap">{meta.ip || '-'}</td>
        <td className="px-3 py-2">
          {meta.business_success === true && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
          {meta.business_success === false && <AlertCircle className="w-4 h-4 text-red-600" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50">
          <td colSpan={8} className="px-6 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h4 className="text-[11px] font-bold uppercase text-gray-500 mb-2 tracking-wide">Request</h4>
                <div className="space-y-2">
                  <div className="text-xs text-gray-700">
                    <span className="font-semibold">Query:</span> <span className="font-mono">{Object.keys(meta.query_params || {}).length ? JSON.stringify(meta.query_params) : '(none)'}</span>
                  </div>
                  <div className="text-xs text-gray-700">
                    <span className="font-semibold">User-Agent:</span> <span className="font-mono break-all">{meta.user_agent || '-'}</span>
                  </div>
                  <PrettyJson value={meta.request_body} />
                </div>
              </div>
              <div>
                <h4 className="text-[11px] font-bold uppercase text-gray-500 mb-2 tracking-wide">Response</h4>
                <div className="space-y-2">
                  <div className="text-xs text-gray-700">
                    <span className="font-semibold">Order ID:</span> <span className="font-mono">{meta.order_id || '-'}</span>
                  </div>
                  <div className="text-xs text-gray-700">
                    <span className="font-semibold">Merchant:</span> <span className="font-mono">{meta.merchant_id || '-'}</span>
                  </div>
                  <PrettyJson value={meta.response_body} />
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function MerchantApiLogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({
    merchant_id: '',
    project_id: '',
    method: '',
    path_contains: '',
    only_errors: false,
  });
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filters.merchant_id) params.merchant_id = filters.merchant_id;
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.method) params.method = filters.method;
      if (filters.path_contains) params.path_contains = filters.path_contains;
      if (filters.only_errors) params.only_errors = true;
      params.limit = 200;

      const res = await axios.get(`${BACKEND_URL}/api/super-admin/merchant-api-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLogs(res.data || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filters]);

  useEffect(() => {
    if (!autoRefresh) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(load, REFRESH_INTERVAL_MS);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [autoRefresh, filters]);

  const filtered = useMemo(() => {
    if (!search.trim()) return logs;
    const q = search.toLowerCase();
    return logs.filter(l => {
      const m = l.metadata || {};
      return (
        (m.path || '').toLowerCase().includes(q) ||
        (m.merchant_id || '').toLowerCase().includes(q) ||
        (m.project_name || '').toLowerCase().includes(q) ||
        (m.order_id || '').toLowerCase().includes(q) ||
        (m.ip || '').toLowerCase().includes(q) ||
        JSON.stringify(m.request_body || '').toLowerCase().includes(q) ||
        JSON.stringify(m.response_body || '').toLowerCase().includes(q)
      );
    });
  }, [logs, search]);

  const stats = useMemo(() => {
    const total = logs.length;
    const errors = logs.filter(l => (l.metadata?.status_code || 0) >= 400).length;
    const ok = total - errors;
    const avgMs = total ? Math.round(logs.reduce((s, l) => s + (l.metadata?.duration_ms || 0), 0) / total) : 0;
    return { total, errors, ok, avgMs };
  }, [logs]);

  return (
    <div data-testid="merchant-api-logs-tab" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Logs API a Comercios</h2>
          <p className="text-sm text-slate-500 mt-1">Visualización en tiempo real de los requests/responses entre Caura Pay y los comercios.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(v => !v)}
            data-testid="merchant-logs-toggle-autorefresh"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${autoRefresh ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
          >
            {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </button>
          <button
            onClick={load}
            data-testid="merchant-logs-refresh"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border bg-white hover:bg-slate-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-white p-4">
          <div className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Total</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-[11px] uppercase text-emerald-600 font-semibold tracking-wide">2xx / 3xx</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.ok}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-[11px] uppercase text-red-600 font-semibold tracking-wide">4xx / 5xx</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{stats.errors}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Latencia prom.</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.avgMs} <span className="text-xs font-normal text-slate-500">ms</span></div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-white p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Filter className="w-4 h-4" /> Filtros</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input
            type="text"
            placeholder="Merchant ID"
            value={filters.merchant_id}
            onChange={e => setFilters(f => ({ ...f, merchant_id: e.target.value }))}
            data-testid="merchant-logs-filter-merchant-id"
            className="px-3 py-1.5 border rounded-lg text-sm font-mono"
          />
          <input
            type="text"
            placeholder="Project ID"
            value={filters.project_id}
            onChange={e => setFilters(f => ({ ...f, project_id: e.target.value }))}
            data-testid="merchant-logs-filter-project-id"
            className="px-3 py-1.5 border rounded-lg text-sm font-mono"
          />
          <select
            value={filters.method}
            onChange={e => setFilters(f => ({ ...f, method: e.target.value }))}
            data-testid="merchant-logs-filter-method"
            className="px-3 py-1.5 border rounded-lg text-sm"
          >
            <option value="">Cualquier método</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </select>
          <input
            type="text"
            placeholder="Path contiene… (ej. debit)"
            value={filters.path_contains}
            onChange={e => setFilters(f => ({ ...f, path_contains: e.target.value }))}
            data-testid="merchant-logs-filter-path"
            className="px-3 py-1.5 border rounded-lg text-sm"
          />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={filters.only_errors}
              onChange={e => setFilters(f => ({ ...f, only_errors: e.target.checked }))}
              data-testid="merchant-logs-filter-errors"
              className="w-4 h-4"
            />
            Solo errores (4xx/5xx)
          </label>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar en payloads, IP, order_id…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="merchant-logs-search"
              className="w-full pl-9 pr-9 py-1.5 border rounded-lg text-sm"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Error cargando logs: {String(error)}
        </div>
      )}

      <div className="rounded-xl border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 text-left font-semibold">Timestamp</th>
                <th className="px-3 py-2 text-left font-semibold">Método</th>
                <th className="px-3 py-2 text-left font-semibold">Path</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Lat.</th>
                <th className="px-3 py-2 text-left font-semibold">Proyecto</th>
                <th className="px-3 py-2 text-left font-semibold">IP</th>
                <th className="px-3 py-2 text-left font-semibold">OK</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-500">
                    No hay logs que coincidan con los filtros.
                  </td>
                </tr>
              )}
              {filtered.map(log => (
                <LogRow
                  key={log.log_id}
                  log={log}
                  expanded={expandedId === log.log_id}
                  onToggle={() => setExpandedId(expandedId === log.log_id ? null : log.log_id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
